import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import { Add, Save, Close, ExpandMore, ExpandLess } from '@mui/icons-material';
import OfficerAddDialog from '../../registration/company_information/add_officer_dialog';
import OfficerItem from '../../registration/company_information/officer_item';

const rolePluralMap = {
  director: 'directors',
  secretary: 'secretaries',
  member: 'members',
};

const OfficerManagementDialog = ({ open, onClose, company, onSave }) => {
  const [localOfficers, setLocalOfficers] = useState({
    directors: [],
    secretaries: [],
    members: [],
    ...company.Officers,
  });

  const [expandedSections, setExpandedSections] = useState({
    directors: true,
    secretaries: true,
    members: true,
  });

  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Reset local state when dialog opens
  useEffect(() => {
    if (open) {
      setLocalOfficers({
        directors: [],
        secretaries: [],
        members: [],
        ...company.Officers,
      });
      setExpandedSections({
        directors: true,
        secretaries: true,
        members: true,
      });
    }
  }, [open, company.Officers]);

  const handleAddOfficer = (newOfficer) => {
    const roleKey = rolePluralMap[newOfficer.role.toLowerCase()];
    if (!roleKey) {
      console.error(`Unknown officer role: ${newOfficer.role}`);
      return;
    }

    setLocalOfficers((prev) => ({
      ...prev,
      [roleKey]: [
        ...(prev[roleKey] || []),
        {
          ...newOfficer,
          id: `${newOfficer.role.slice(0, 3)}_${crypto.randomUUID()}`,
        },
      ],
    }));
    setAddDialogOpen(false);
  };

  const handleRemoveOfficer = (role, index) => {
    setLocalOfficers((prev) => {
      const updatedOfficers = { ...prev };
      updatedOfficers[role].splice(index, 1);
      return updatedOfficers;
    });
  };

  const handleSave = () => {
    onSave({ ...company, Officers: localOfficers });
    onClose();
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Count all officers
  const totalOfficers = Object.values(localOfficers).reduce(
    (sum, officers) => sum + (officers?.length || 0),
    0
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{
          '& .MuiDialog-paper': {
            height: '80vh',
          },
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" component="div">
              Manage Officers
              <Chip
                label={`${totalOfficers} total`}
                size="small"
                color="primary"
                sx={{ ml: 2 }}
              />
            </Typography>
            <Box>
              <Tooltip title="Add new officer">
                <IconButton
                  onClick={() => setAddDialogOpen(true)}
                  color="primary"
                  sx={{ mr: 1 }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Officers List */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
              }}
            >
              {totalOfficers > 0 ? (
                <>
                  {localOfficers.directors?.length > 0 && (
                    <>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('directors')}
                      >
                        <Typography variant="subtitle1">
                          Directors ({localOfficers.directors.length})
                        </Typography>
                        <IconButton size="small">
                          {expandedSections.directors ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Box>
                      <Collapse in={expandedSections.directors}>
                        {localOfficers.directors.map((officer, index) => (
                          <OfficerItem
                            key={officer.id}
                            officer={officer}
                            index={index}
                            registeredAddress={company.RegisteredAddress}
                            onRemove={() =>
                              handleRemoveOfficer('directors', index)
                            }
                          />
                        ))}
                      </Collapse>
                    </>
                  )}

                  {localOfficers.secretaries?.length > 0 && (
                    <>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('secretaries')}
                      >
                        <Typography variant="subtitle1">
                          Secretaries ({localOfficers.secretaries.length})
                        </Typography>
                        <IconButton size="small">
                          {expandedSections.secretaries ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Box>
                      <Collapse in={expandedSections.secretaries}>
                        {localOfficers.secretaries.map((officer, index) => (
                          <OfficerItem
                            key={officer.id}
                            officer={officer}
                            index={index}
                            registeredAddress={company.RegisteredAddress}
                            onRemove={() =>
                              handleRemoveOfficer('secretaries', index)
                            }
                          />
                        ))}
                      </Collapse>
                    </>
                  )}

                  {localOfficers.members?.length > 0 && (
                    <>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('members')}
                      >
                        <Typography variant="subtitle1">
                          Members ({localOfficers.members.length})
                        </Typography>
                        <IconButton size="small">
                          {expandedSections.members ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Box>
                      <Collapse in={expandedSections.members}>
                        {localOfficers.members.map((officer, index) => (
                          <OfficerItem
                            key={officer.id}
                            officer={officer}
                            index={index}
                            registeredAddress={company.RegisteredAddress}
                            onRemove={() =>
                              handleRemoveOfficer('members', index)
                            }
                          />
                        ))}
                      </Collapse>
                    </>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color="text.secondary">
                    No officers found. Click the + button to add one.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined" sx={{ minWidth: 120 }}>
            Discard
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<Save />}
            sx={{ minWidth: 120 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <OfficerAddDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddOfficer}
        companyAddress={company.RegisteredAddress}
        companyType={company.type}
      />
    </>
  );
};

export default OfficerManagementDialog;
